import React, { useCallback, useMemo } from "react";
import { Typography, Empty, Button } from "antd";
import styled from "@emotion/styled";
import Select from "../generic/Select";
import { getFullName } from "../../utils/shared";
import Avatar from "../generic/Avatar";
import { DeleteOutlined } from "@ant-design/icons";

const { Text } = Typography;

const StyledUserSelect = styled.div`
  width: 100%;

  .list-container {
    margin-top: 16px;
  }

  .list-title {
    display: block;
    margin-bottom: 8px;
  }

  .list-scroll {
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .member-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    margin-top: 5px;
    background: #f5f5f5;
    transition: background 0.2s ease;
    .anticon {
      font-size: 18px;
    }
  }

  .member-item:hover {
    background: #f5f5f5;
  }

  .member-details {
    flex: 1;
    min-width: 0;
  }

  .member-name {
    font-size: 15px;
    font-weight: 500;
    color: #262626;
  }

  .member-email {
    font-size: 14px;
    color: #8c8c8c;
  }
`;

const UserSelect = ({
  users = [],
  value,
  onChange,
  multiple = true,
  showList = true,
  disabled = false,
  allowClear = true,
  maxTagCount = 1,
  placeholder = "Select users",
  listTitle = "Selected Users",
}) => {
  const selectedUsers = useMemo(() => {
    if (!value) return [];

    return users.filter((user) =>
      multiple ? value.includes(user.id) : user?.id === value
    );
  }, [value, users, multiple]);

  const handleRemoveUser = useCallback(
    (userId) => {
      if (multiple) {
        const newValue = value.filter((id) => id !== userId);
        onChange(newValue);
      } else {
        onChange(null);
      }
    },
    [multiple, onChange, value]
  );

  return (
    <StyledUserSelect>
      {/* Select Dropdown */}
      <Select
        mode={multiple ? "multiple" : undefined}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        allowClear={allowClear}
        maxTagCount={maxTagCount}
        disabled={disabled}
        onSelect={() => {
          if (multiple) {
            //@ts-ignore
            document.activeElement.blur();
          }
        }}
        options={users.map((user) => ({
          value: user.id,
          label: getFullName(user) + " - " + user.email,
          disabled: user?.disabled,
        }))}
        style={{ width: "100%" }}
      />

      {/* Selected Users List */}
      {showList && selectedUsers.length > 0 && (
        <div className="list-container">
          <Text strong className="list-title">
            {listTitle} ({selectedUsers.length})
          </Text>
          <div className="list-scroll">
            {selectedUsers.map((member) => (
              <div key={member.id} className="member-item ">
                <Avatar user={member} size={40} />

                <div className="member-details">
                  <div className="member-name ellipsis-text">
                    {getFullName(member)}
                  </div>
                  <div className="member-email ellipsis-text">
                    {member.email}
                  </div>
                </div>
                <Button
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveUser(member?.id)}
                  type="text"
                  danger
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when showList is true but no users selected */}
      {showList && selectedUsers.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No users selected"
          className="empty-state"
        />
      )}
    </StyledUserSelect>
  );
};

export default UserSelect;
